import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogTrigger, DialogClose } from '@/components/ui/dialog'
import { useUnsavedChangesPrompt } from '@/hooks/use-unsaved-changes'
import { toast } from 'sonner'
import { TOASTS } from '@/data/toast-messages'
import { ToastMessage } from '../../../components/toast-message'
import RulesForm from './rules-form'
import { Divider } from '@/components/divider'
import { Icons } from '@/theme/Images'
import RulesDialogHeader from '../dialog-header/rules-dialog-header'
import ConfirmationDialog from '../confirmation-dialog/confirmation-dialog'
import { initialValues, validationSchema } from './rules-config'
import { useFormik } from 'formik'
import { useCreateRule } from '../../hooks/use-rewards-rule'
import { useFetchAllRewardsGroups } from '@/pages/hooks/use-rewards-group'

const CreateRules = () => {
  //Helps in managing unsaved changes*
  const {
    setUnsavedChanges, //sets a flag indicating whether there are unsaved changes
    showConfirmation, //displays a confirmation prompt-
    handleConfirm, //handles the confirmation--
    handleCancel, //handles the cancellation
    promptIfUnsaved, //checks if there are unsaved changes & prompts  user to confirm or cancel
  } = useUnsavedChangesPrompt(() => {
    setOpen(false)
  })

  // Fetch group descriptions
  const { data: allGroups } = useFetchAllRewardsGroups()
  const groupOptions =
    allGroups?.map(group => ({
      label: group.ruleGroup,
      value: group.ruleGroup,
    })) || []

  const [open, setOpen] = useState(false)

  const [ruleName, setRuleName] = useState('')
  const [checkedCategoryItems, setCheckedCategoryItems] = useState({})
  const [searchTermCategory, setSearchTermCategory] = useState('')

  const handleRuleInputChange = (id, value) => {
    setRuleName(value)
    setUnsavedChanges(true)
  }

  //Updates the checked items for MCC, MID, or CID based on the user's selection
  const handleCheckboxChange = (type, index) => {
    const setCheckedItems = {
      category: setCheckedCategoryItems,
    }

    setCheckedItems[type](prev => ({
      ...prev,
      [index]: !prev[index],
    }))
    setUnsavedChanges(true)
  }

  const handleSearchChangeCategory = value => {
    setSearchTermCategory(value)
    setUnsavedChanges(true)
  }

  const recurringConvertToBoolean = display => (display === 'Yes' ? true : false)

  const onlineConvertToBoolean = display => {
    if (display === 'online') {
      return true
    } else if (display === 'in-store') {
      return false
    } else {
      return null
    }
  }

  const { mutate: createRule } = useCreateRule()
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      const transformedValues = {
        ruleId: values.description,
        description: values.description,
        displayText: values.description,
        displayType: values.description,
        shortDisplayText: values.description,
        ruleGroup: values.ruleGroup || null,
        rewardClassList: Object.keys(checkedCategoryItems).filter(key => checkedCategoryItems[key]),
        recurring: recurringConvertToBoolean(values.recurring), // Convert 'Yes' or 'No' to boolean
        recurrencePeriod: values.recurrencePeriod,
        startDate: values.startDate || new Date(),
        endDate: values.endDate || null,
        rulePriority: parseFloat(values.rulePriority) || 0,
        pointMultiplier: parseInt(values.pointMultiplier) || null,
        optIn: values.optIn === 'true',
        singleTransaction: values.singleTransaction === 'true',
        eventType: values.eventType,
        ledgerType: values.ledgerType,
        pointsRequired: values.pointsRequired === 'true',
        maxPoints: parseInt(values.maxPoints) || null,
        minimumCount: parseInt(values.minimumCount) || 0,
        maxCount: parseInt(values.maxCount) || null,
        minimumSpend: parseInt(values.minimumSpend) || null,
        fixedPoints: parseInt(values.fixedPoints) || null,
        displayScore: parseInt(values.displayScore) || null,
        daysAfterActivation: parseInt(values.daysAfterActivation) || null,
        mccInclusionList: Array.isArray(values.mccInclusionList) ? values.mccInclusionList : [values.mccInclusionList],
        mccExclusionList: Array.isArray(values.mccExclusionList) ? values.mccExclusionList : [values.mccExclusionList],
        midInclusionList: Array.isArray(values.midInclusionList) ? values.midInclusionList : [values.midInclusionList],
        midExclusionList: Array.isArray(values.midExclusionList) ? values.midExclusionList : [values.midExclusionList],
        chainInclusionList: Array.isArray(values.chainInclusionList)
          ? values.chainInclusionList
          : [values.chainInclusionList],
        chainExclusionList: Array.isArray(values.chainExclusionList)
          ? values.chainExclusionList
          : [values.chainExclusionList],
        online: onlineConvertToBoolean(values.online),
        wallet: Array.isArray(values.wallet) ? values.wallet : [values.wallet],
      }
      createRule(transformedValues, {
        onSuccess: response => {
          if (!response?.ruleId) {
            setOpen(false)
            console.error('Failed to create rule: Missing ruleId in response.')
            toast.custom(() => <ToastMessage {...TOASTS.CREATE_RULES.RULES_CREATE_FAILURE} />)
            return
          }
          setCheckedCategoryItems({})
          setOpen(false)
          toast.custom(() => <ToastMessage {...TOASTS.CREATE_RULES.RULES_CREATE_SUCCESS} />)
        },
        onError: error => {
          console.error('Error creating rule:', error)
          toast.custom(() => <ToastMessage {...TOASTS.CREATE_RULES.RULES_CREATE_FAILURE} />)
        },
      })
    },
  })
  const resetCheckboxes = () => {
    setCheckedCategoryItems({})
  }

  const resetSearchTerms = () => {
    setSearchTermCategory('')
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) {
            promptIfUnsaved() // Prompt if there are unsaved changes
          } else {
            setUnsavedChanges(false) // Reset unsaved changes if dialog is opened
            formik.resetForm()
          }
        }}
      >
        <DialogTrigger asChild>
          <Button
            className="bg-brand-600 hover:bg-brand-700 md:w-auto h-[36px] text-sm text-white"
            onClick={() => setOpen(true)}
          >
            <img src={Icons.plusIcon} alt="icon" height={11.67} width={11.67} />
            <span className="font-semibold text-sm ml-2">Add Rule</span>
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-[880px] h-[98vh] overflow-y-auto overflow-x-hidden">
          <RulesDialogHeader
            title="Add rewards rule"
            description="Add a new rule for cardholders to earn points."
            icon={Icons.addRewardsRule}
          />
          <div className="ml-[-26px] w-[105%]">
            <Divider />
          </div>

          <RulesForm
            ruleName={ruleName}
            checkedCategoryItems={checkedCategoryItems}
            searchCategories={searchTermCategory}
            onRuleInputChange={handleRuleInputChange}
            onCheckboxChange={handleCheckboxChange}
            onSearchChangeCategory={handleSearchChangeCategory}
            onCancel={() => setOpen(false)}
            formik={formik}
            groupOptions={groupOptions}
          />

          <div className="ml-[-26px] w-[105%] mt-10">
            <Divider />
          </div>

          {/* Confirmation dialog */}
          <ConfirmationDialog
            isOpen={showConfirmation}
            onClose={handleCancel}
            onConfirm={() => {
              handleConfirm()
              resetCheckboxes()
              resetSearchTerms()
            }}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="outline"
                className="w-full md:w-[410px] h-[44px] mb-[-6px] text-[#344054] mt-1"
                onClick={resetSearchTerms}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              className="text-[#FFFFFF] w-full md:w-[410px] h-[44px] mb-[-6px] mt-1"
              onClick={() => formik.handleSubmit()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CreateRules
